'use client';

import { Link } from 'react-router-dom';
import { JsonLd } from '@/seo/JsonLd';
import type { Product } from '@/data/products';
import { products } from '@/data/products';
import { ProductCard } from '@/components/ProductCard/ProductCard';
import { Accordion } from '@/components/Accordion/Accordion';
import { Tabs } from '@/components/Tabs/Tabs';
import { IconWhatsApp, IconPhone, IconStar } from '@/components/Icons/Icons';
import { buildWhatsAppUrl, SALES_PHONE_READABLE } from '@/utils/whatsapp';
import { SITE_DOMAIN, toAbsoluteUrl } from '@/utils/url';
import styles from './ProductDetail.module.css';

const silivriProduct: Product = {
  slug: 'silivri-bondex-sunger',
  name: 'Silivri Bondex Sünger',
  category: 'Bondex Sünger',
  shortDescription:
    'Silivri bondex sünger; konut, ofis, stüdyo ve proje bazlı uygulamalarda ses yalıtımı için yüksek yoğunluklu çözüm sunar.',
  description:
    'Silivri bondex sünger; rebonded süngerpan yapısıyla darbe ve titreşim kaynaklı sesleri azaltmaya yardımcı olan, Silivri içi projelere özel ses yalıtım çözümüdür.',
  imagePath: '/images/bondex-8.webp',
  sku: 'BDX-SLV',
  mpn: 'BDX-SLV',
  priceTRY: 130,
  priceUnit: 'm2',
  faqs: [
    {
      question: 'Silivri bondex sünger nedir?',
      answer:
        'Silivri bondex sünger, yüksek yoğunluklu rebonded süngerpan malzemedir. Darbe ve titreşim kaynaklı ses etkisini düşürmek için zemin, duvar ve teknik detaylarda kullanılır.',
    },
    {
      question: 'Silivri içinde teslimat yapıyor musunuz?',
      answer:
        'Evet. Silivri merkez ve çevre mahallelerde proje planına bağlı olarak hızlı teslimat ve etaplı sevkiyat desteği sağlanır.',
    },
    {
      question: 'Kalınlık seçimi nasıl yapılır?',
      answer:
        'Kalınlık seçimi, hedef konfor seviyesi, mevcut yapı detayı ve şikayetin tipine göre yapılır. İnce detaylarda düşük kalınlık, zorlu senaryolarda daha güçlü katmanlar önerilir.',
    },
    {
      question: 'Silivri bondex sünger fiyatı nasıl hesaplanır?',
      answer:
        'Fiyat hesaplaması metrekare bazında yapılır. Kalınlık, yoğunluk, toplam metraj ve uygulama kurgusu toplam maliyeti etkiler.',
    },
    {
      question: 'Uygulama desteği veriyor musunuz?',
      answer:
        'Evet. Keşif, metraj doğrulama, ürün seçimi, uygulama planı ve süreç takibi için teknik yönlendirme verilir.',
    },
  ],
  reviews: [
    {
      authorName: 'Serkan Yılmaz',
      datePublished: '2026-04-09',
      ratingValue: 5,
      title: 'Silivri konut projesinde iyi sonuç',
      body:
        'Katlar arası darbe sesi için uygulama yaptık. Gürültü seviyesinde belirgin iyileşme oldu.',
      verified: true,
    },
    {
      authorName: 'Banu Demir',
      datePublished: '2026-04-13',
      ratingValue: 5,
      title: 'Ofis bölmelerinde etkili',
      body:
        'Toplantı odası geçişlerinde ses taşmasını azaltmak için kullandık, beklentimizi karşıladı.',
      verified: true,
    },
    {
      authorName: 'Murat Kaya',
      datePublished: '2026-04-18',
      ratingValue: 4,
      title: 'Stüdyo uygulamasında dengeli performans',
      body:
        'Kayıt alanında daha kontrollü bir ses ortamı elde ettik, süreç planlaması da düzgündü.',
      verified: true,
    },
  ],
};

export function CityBondexSilivri() {
  const url = toAbsoluteUrl('/silivri-bondex-sunger');
  const title = 'Silivri Bondex Sünger | Yalıtım Süngeri– Echopan';
  const description =
    'Silivri bondex sünger, konut ve ticari yapılarda darbe ve titreşim kaynaklı sesleri azaltmaya yardımcı olur. Echopan kalitesiyle sunulur.';

  const relatedSlugs = ['bondex-sunger', 'bondex-sunger-20mm', 'bondex-sunger-40mm', 'bariyerli-bondex-sunger'];
  const relatedBondex = relatedSlugs.map((slug) => products.find((p) => p.slug === slug)).filter((p): p is Product => Boolean(p));

  const waUrl = buildWhatsAppUrl('Merhaba, Silivri bondex sünger için fiyat ve teslimat bilgisi rica ediyorum. (silivri-bondex-sunger)');
  const displayPrice = silivriProduct.priceTRY
    ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(silivriProduct.priceTRY)
    : null;

  const pageReviews =
    silivriProduct.reviews?.map((r) => ({
      name: r.authorName,
      date: r.datePublished,
      title: r.title,
      verified: !!r.verified,
      rating: r.ratingValue,
      text: r.body,
    })) || [];
  const avgRating =
    pageReviews.length > 0 ? pageReviews.reduce((sum, r) => sum + (Number.isFinite(r.rating) ? r.rating : 0), 0) / pageReviews.length : 0;

  const topSpecs = [
    { k: 'Stok', v: 'Silivri içi düzenli stok' },
    { k: 'Teslim', v: '1-3 iş günü' },
    { k: 'Kalınlık', v: '10mm-50mm' },
    { k: 'Uygulama', v: 'Yapıştırma / Mekanik tespit' },
    { k: 'Hizmet', v: 'Silivri merkez + çevre' },
    { k: 'Destek', v: 'Teknik danışmanlık + keşif' },
  ];

  const breadcrumb = [
    { name: 'Anasayfa', item: toAbsoluteUrl('/') },
    { name: 'Ürünler', item: toAbsoluteUrl('/urunler') },
    { name: 'Silivri Bondex Sünger', item: url },
  ];

  const validFromStr = new Date().toISOString().split('T')[0];
  const priceValidUntil = new Date();
  priceValidUntil.setFullYear(priceValidUntil.getFullYear() + 1);
  const priceValidUntilStr = priceValidUntil.toISOString().split('T')[0];

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${toAbsoluteUrl('/')}#website`,
        name: 'Bondex Sünger',
        url: toAbsoluteUrl('/'),
        inLanguage: 'tr-TR',
        potentialAction: {
          '@type': 'SearchAction',
          target: { '@type': 'EntryPoint', urlTemplate: `${toAbsoluteUrl('/')}urunler?q={search_term_string}` },
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'Organization',
        '@id': `${toAbsoluteUrl('/')}#organization`,
        name: 'bariyerlibondexsunger.com',
        url: toAbsoluteUrl('/'),
        logo: { '@type': 'ImageObject', url: toAbsoluteUrl('/images/bondex-logo.webp'), width: 512, height: 512 },
        contactPoint: [
          { '@type': 'ContactPoint', telephone: '+90 216 519 34 11', contactType: 'customer service', areaServed: 'TR', availableLanguage: ['tr'] },
          { '@type': 'ContactPoint', telephone: '+90 546 919 11 34', contactType: 'sales', areaServed: 'TR', availableLanguage: ['tr'] },
        ],
        sameAs: [
          'https://www.facebook.com/profile.php?id=61575206163572',
          'https://x.com/KabinPods',
          'https://www.instagram.com/kabinpods/',
          'https://www.youtube.com/channel/UCKk3RjOabrJb8JQYK9BjWkw',
          'https://www.linkedin.com/company/tasar%C4%B1m-akustik-mimarl%C4%B1k',
        ],
      },
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        url,
        name: title,
        description,
        isPartOf: { '@id': `${toAbsoluteUrl('/')}#website` },
        breadcrumb: { '@id': `${url}#breadcrumb` },
        inLanguage: 'tr-TR',
        datePublished: '2026-04-27',
        dateModified: new Date().toISOString().split('T')[0],
        primaryImageOfPage: { '@type': 'ImageObject', url: toAbsoluteUrl(silivriProduct.imagePath), width: 1200, height: 750 },
        about: [
          { '@type': 'Thing', name: 'Silivri Bondex Sünger' },
          { '@type': 'Thing', name: 'ses yalıtım süngeri' },
          { '@type': 'Thing', name: 'darbe sesi izolasyonu' },
        ],
        speakable: { '@type': 'SpeakableSpecification', cssSelector: ['.ProductSEO', '.ProductPage h1'] },
        mainEntity: { '@id': `${url}#product` },
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${url}#breadcrumb`,
        itemListElement: breadcrumb.map((b, idx) => ({ '@type': 'ListItem', position: idx + 1, name: b.name, item: b.item })),
      },
      {
        '@type': 'Product',
        '@id': `${url}#product`,
        name: silivriProduct.name,
        image: [{ '@type': 'ImageObject', url: toAbsoluteUrl(silivriProduct.imagePath), width: 1200, height: 750 }],
        description: silivriProduct.description,
        brand: { '@type': 'Brand', name: 'Bondex' },
        category: silivriProduct.category,
        url,
        sku: silivriProduct.sku,
        mpn: silivriProduct.mpn,
        productID: silivriProduct.sku || silivriProduct.mpn,
        material: 'Rebonded Süngerpan',
        audienceType: 'Business',
        additionalProperty: [
          { '@type': 'PropertyValue', name: 'Kategori', value: silivriProduct.category },
          { '@type': 'PropertyValue', name: 'Tip', value: 'Rebonded Süngerpan' },
          { '@type': 'PropertyValue', name: 'Hizmet Bölgesi', value: 'Silivri' },
        ],
        offers: {
          '@type': 'Offer',
          url,
          priceCurrency: 'TRY',
          price: String(silivriProduct.priceTRY),
          validFrom: validFromStr,
          priceValidUntil: priceValidUntilStr,
          availability: 'https://schema.org/InStock',
          itemCondition: 'https://schema.org/NewCondition',
          seller: { '@id': `${toAbsoluteUrl('/')}#organization` },
          unitText: 'm²',
          priceSpecification: { '@type': 'UnitPriceSpecification', price: String(silivriProduct.priceTRY), priceCurrency: 'TRY', unitCode: 'MTK', unitText: 'm²' },
          hasMerchantReturnPolicy: { '@id': `${url}#merchantReturnPolicy` },
          shippingDetails: { '@id': `${url}#shippingDetails` },
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: avgRating.toFixed(1),
          reviewCount: pageReviews.length,
          bestRating: '5',
          worstRating: '1',
        },
        review: silivriProduct.reviews?.map((r) => ({
          '@type': 'Review',
          author: { '@type': 'Person', name: r.authorName },
          datePublished: r.datePublished,
          reviewBody: r.body,
          headline: r.title,
          ...(r.verified && { reviewAspect: 'Verification' }),
          reviewRating: { '@type': 'Rating', ratingValue: String(r.ratingValue), bestRating: '5', worstRating: '1' },
        })),
      },
      {
        '@type': 'MerchantReturnPolicy',
        '@id': `${url}#merchantReturnPolicy`,
        applicableCountry: { '@type': 'Country', name: 'TR' },
        returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
        merchantReturnDays: 14,
        returnMethod: 'https://schema.org/ReturnByMail',
        returnFees: 'https://schema.org/FreeReturn',
      },
      {
        '@type': 'OfferShippingDetails',
        '@id': `${url}#shippingDetails`,
        shippingRate: { '@type': 'MonetaryAmount', value: '0', currency: 'TRY' },
        shippingDestination: { '@type': 'DefinedRegion', addressCountry: { '@type': 'Country', name: 'TR' } },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          handlingTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 3, unitCode: 'DAY' },
          transitTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 5, unitCode: 'DAY' },
          businessDays: { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'] },
          cutoffTime: '14:00',
        },
      },
      {
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        mainEntity: silivriProduct.faqs?.map((f) => ({ '@type': 'Question', name: f.question, acceptedAnswer: { '@type': 'Answer', text: f.answer } })),
      },
    ],
  };

  const tabItems = [
    {
      key: 'aciklama',
      label: 'Açıklama',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Silivri Bondex Sünger Nedir, Nerelerde Kullanılır?</h2>
            <h3 className={styles.seoSubtitle}>Silivri’de ses yalıtımı için Bondex sünger tercih edilir mi?</h3>
            <p className={styles.seoText}>
              Silivri'de ses yalıtımı konusu, ilçe yapısının karma olması nedeniyle standart şehir merkezlerinden farklı değerlendirilmelidir.
              İlçede bir tarafta apartman ve site yaşamı yoğunlaşırken, diğer tarafta müstakil konut, atölye, küçük ölçekli üretim alanı ve
              sezonluk kullanılan yapılar bir aradadır. Bu çeşitlilik, ses şikayetlerinin de tek bir tipte olmayacağını gösterir. Üst kat
              kaynaklı darbe sesi, komşu duvar konuşma geçişi, teknik hacimden gelen uğultu ve zaman zaman dış çevre etkisi birlikte hissedilebilir.
            </p>
            <p className={styles.seoText}>
              Bondex sünger bu noktada, özellikle darbe ve titreşim kaynaklı enerji transferini düşürmeye yardımcı olan bir katman çözümüdür.
              Rebonded süngerpan yapısı sayesinde yoğunluğu yüksek bir tampon etkisi oluşturur. Ancak burada önemli olan yalnızca "bondex var mı"
              sorusu değil; "doğru yerde doğru kalınlıkla kullanılıyor mu" sorusudur. Silivri projelerinde kalıcı sonuç için ürün seçimi ile
              uygulama kalitesi birlikte ele alınmalıdır.
            </p>
            <h3 className={styles.seoSubtitle}>Ev, ofis ve stüdyo için uygun kullanım alanları</h3>
            <p className={styles.seoText}>
              Ev tarafında en sık ihtiyaç, katlar arası darbe etkisinin ve komşu daire konuşma geçişinin azaltılmasıdır. Özellikle yatak odası,
              çocuk odası ve çalışma odası gibi hassas hacimlerde kullanıcı memnuniyeti, doğru katman planı ile ciddi şekilde yükselir.
              Ofis tarafında ise toplantı mahremiyeti ve açık ofis uğultusunun yönetimi öne çıkar. Stüdyo kullanımında hedef yalnızca dışarı
              ses kaçışını azaltmak değil, içeride daha dengeli bir çalışma ortamı oluşturmaktır.
            </p>
            <p className={styles.seoText}>
              Bu üç kullanım tipinin her biri farklı teknik önceliğe sahip olduğu için tek bir şablonla ilerlemek doğru değildir. Evde konfor
              öncelikli yaklaşım, ofiste iletişim mahremiyeti öncelikli yaklaşım, stüdyoda ise daha hassas akustik denge yaklaşımı gerekir.
              Silivri'deki projelerde çözüm planı yapılırken bu ayrım net şekilde belirlenmelidir.
            </p>
            <h3 className={styles.seoSubtitle}>Yoğunluk ve sertlik değerleri ne anlama gelir?</h3>
            <p className={styles.seoText}>
              Kullanıcıların en çok sorduğu başlıklardan biri yoğunluk değeridir. Yoğunluk arttıkça malzemenin belirli frekans bantlarındaki
              davranışı değişir ve darbe kaynaklı enerjiye verdiği tepki farklılaşır. Ancak "en yüksek yoğunluk en iyi sonuç" yaklaşımı her
              projede doğru değildir. Çünkü sistem performansı sadece yoğunlukla değil, katman düzeni ve uygulama kalitesiyle belirlenir.
            </p>
            <p className={styles.seoText}>
              Sertlik değeri de uygulama alanına göre önem kazanır. Çok sert veya çok yumuşak davranış gösteren bir yapı, yanlış senaryoda
              beklenmeyen sonuç verebilir. Bu nedenle teknik ekip değerlendirmesi olmadan sadece ürün kodu üzerinden karar vermek risklidir.
              Sağlıklı yaklaşım, şikayet tipini tanımlayıp uygun yoğunluk-sertlik kombinasyonunu projeye göre seçmektir.
            </p>

            <h2 className={styles.seoTitle}>Silivri Bondex Sünger Çeşitleri ve Kalınlık Seçenekleri</h2>
            <h3 className={styles.seoSubtitle}>10 mm, 20 mm, 30 mm, 50 mm Bondex sünger farkları</h3>
            <p className={styles.seoText}>
              10 mm çözümler, yükseklik kısıtı olan veya ince katman gereken alanlarda tercih edilir. 20 mm, çoğu konut/ofis ihtiyacında
              maliyet-performans dengesi açısından güvenli bir başlangıç seviyesi sunar. 30 mm, orta-yüksek şikayetli senaryolarda daha güçlü
              sönümleme etkisi verir. 50 mm ise zorlayıcı teknik alanlar veya daha agresif şikayetlerde değerlendirilir.
            </p>
            <p className={styles.seoText}>
              Bu farklar sadece kağıt üstü milimetre değeri değildir; uygulama hedefi açısından ciddi sonuç farkı üretir. Örneğin üst kat
              adım sesi şikayetinde 20 mm yeterli olabilirken, teknik oda titreşim etkisinde 30-50 mm aralığına geçmek gerekebilir. Doğru
              seçim için şikayetin frekans karakteri ve yüzey yapısı birlikte değerlendirilmelidir.
            </p>
            <h3 className={styles.seoSubtitle}>Yüksek yoğunluklu Bondex sünger ne sağlar?</h3>
            <p className={styles.seoText}>
              Yüksek yoğunluklu bondex sünger, özellikle darbe ve titreşim transferinin azaltılmasında güçlü bir referans noktasıdır. Bu
              ürün sınıfı, doğru sistem kurgusuyla birlikte uygulandığında komşu hacimlere taşınan rahatsızlığı düşürmeye yardımcı olur.
              Ancak tek başına yoğunluğu yükseltmek çözümün tamamı değildir; birleşim kalitesi düşükse veya kenar sürekliliği bozulursa
              performans hedefi aşağı çekilebilir.
            </p>
            <p className={styles.seoText}>
              Bu nedenle yüksek yoğunluklu ürünlerin en doğru kullanım alanı, teknik analizle belirlenmiş kritik noktalar olmalıdır.
              Projeye yayılan bütüncül bir planla birlikte uygulandığında hem teknik sonuç hem kullanıcı memnuniyeti daha yüksek olur.
            </p>
            <h3 className={styles.seoSubtitle}>Bariyerli Bondex sünger ne işe yarar?</h3>
            <p className={styles.seoText}>
              Bariyerli bondex, bazı projelerde hava kaynaklı geçişin azaltılması ve katman sisteminin güçlendirilmesi için önemli bir
              alternatiftir. Özellikle daha yüksek izolasyon hedefi olan senaryolarda standart bondex katmanına ek olarak bariyerli yapı
              değerlendirilebilir. Bu karar, teknik hedef ve bütçe dengesine göre verilmelidir.
            </p>
            <p className={styles.seoText}>
              Karşılaştırma yaparken yalnızca ürün fiyatına değil, toplam sistem performansına bakmak gerekir. Bazı projelerde standart
              sistem yeterli olurken, bazı projelerde bariyerli kombinasyon toplam memnuniyeti artırabilir.
            </p>

            <h2 className={styles.seoTitle}>Silivri’de Bondex Sünger Fiyatları Neye Göre Değişir?</h2>
            <h3 className={styles.seoSubtitle}>m² bazlı fiyat hesaplama nasıl yapılır?</h3>
            <p className={styles.seoText}>
              Fiyatlandırma temel olarak metrekare üzerinden yapılır; ancak sadece net m² değeriyle nihai bütçe çıkarmak çoğu zaman yanıltıcıdır.
              Uygulama alanının geometrisi, kesim kaybı, birleşim fazlalığı ve proje içinde birden fazla katman gereksinimi toplam metrajı
              etkileyebilir. Bu nedenle sağlıklı teklif için ölçümün doğru alınması ve uygulama detaylarının metraja yansıtılması gerekir.
            </p>
            <p className={styles.seoText}>
              Özellikle düzensiz planlı alanlarda veya çok sayıda köşe-dönüş içeren uygulamalarda net metraj ile sarf metrajı arasında fark
              oluşabilir. Teknik ekip, bu farkı önceden hesaplayarak teklif sunduğunda proje sırasında sürpriz maliyet çıkma riski azalır.
            </p>
            <h3 className={styles.seoSubtitle}>Kalınlık ve yoğunluk fiyatı nasıl etkiler?</h3>
            <p className={styles.seoText}>
              Kalınlık arttıkça birim maliyet doğal olarak yükselir; yoğunluk arttıkça da ürün sınıfına göre fiyat değişimi görülür. Ancak
              burada kritik olan, en ucuz birim fiyatı seçmek değil, hedeflenen konforu en doğru maliyetle yakalamaktır. Yetersiz kalınlık
              seçimi kısa vadede bütçe avantajı gibi görünse de uygulama sonrasında memnuniyet düşerse toplam maliyet artabilir.
            </p>
            <p className={styles.seoText}>
              Bu nedenle maliyet değerlendirmesi yapılırken "birim fiyat x metraj" hesabına ek olarak performans hedefi ve sürdürülebilir
              kullanım da hesaba katılmalıdır.
            </p>
            <h3 className={styles.seoSubtitle}>Toplu alımlarda maliyet avantajı olur mu?</h3>
            <p className={styles.seoText}>
              Büyük metrajlı projelerde lojistik planlama, üretim/tedarik süreci ve etaplı sevkiyat modeli doğru kurulduğunda maliyet avantajı
              oluşabilir. Toplu alım yalnızca birim fiyat indirimi değil, aynı zamanda iş akışının optimize edilmesi anlamına gelir. Sahada
              doğru etaplama ile malzeme kaybı azalır, uygulama süresi kısalır ve toplam maliyet daha verimli yönetilir.
            </p>
            <p className={styles.seoText}>
              Silivri'de özellikle site, apartman ve çok birimli ticari projelerde toplu planlama yaklaşımı daha yüksek verim sunar. Teknik
              olarak doğru planlanan metraj, gereksiz alım riskini de düşürür.
            </p>

            <h2 className={styles.seoTitle}>Silivri Bondex Sünger ile Ses Yalıtımı Nasıl Yapılır?</h2>
            <h3 className={styles.seoSubtitle}>Duvar ve tavan uygulama yöntemleri</h3>
            <p className={styles.seoText}>
              Duvar ve tavan uygulamalarında temel hedef, sesin zayıf noktalardan geçişini azaltmaktır. Bu noktada yalnızca geniş yüzeyi
              kaplamak değil, birleşim detaylarını doğru çözmek önemlidir. Köşe noktaları, tesisat geçişleri, kapı çevreleri ve tavan-duvar
              birleşimleri kontrol altına alınmadan yapılan uygulamalar beklenen sonucu vermez.
            </p>
            <p className={styles.seoText}>
              Tavan uygulamalarında ise mevcut taşıyıcı yapı ve uygulama yüksekliği dikkate alınmalıdır. Yanlış katman sıralaması hem işçilik
              kalitesini düşürür hem uzun vadede deformasyon riski oluşturur. Bu nedenle sistem, projeye göre teknik planla uygulanmalıdır.
            </p>
            <h3 className={styles.seoSubtitle}>Titreşim ve darbe seslerini kesme performansı</h3>
            <p className={styles.seoText}>
              Darbe ve titreşim kaynaklı etkilerde performans, malzemenin yapısıyla birlikte sistem davranışına bağlıdır. Yalnızca bir katman
              yerleştirmek çoğu zaman yeterli olmaz; geçiş noktalarının tamamı doğru ele alınmalıdır. Uygulama sonrası kullanıcı tarafında
              hissedilen fark, bu bütüncül yaklaşım sayesinde yükselir.
            </p>
            <p className={styles.seoText}>
              Silivri'de darbe şikayetleri yoğun olduğunda, teknik ekip tarafından kaynak odaklı ve katman sürekliliği yüksek bir çözüm
              önerildiğinde sonuç daha kalıcı olur.
            </p>
            <h3 className={styles.seoSubtitle}>Uygulamada dikkat edilmesi gereken noktalar</h3>
            <p className={styles.seoText}>
              Yüzeyin kuru ve temiz olması, doğru yapıştırıcı kullanımı, ek yerlerinin kapatılması, köşe-dönüş sürekliliği ve uygulama sonrası
              kontrol adımları en kritik başlıklardır. Bu adımların biri eksik bırakılırsa tüm sistemin performansı etkilenebilir.
            </p>
            <p className={styles.seoText}>
              Özellikle hızlı teslim baskısı olan projelerde kalite kontrol atlanmamalıdır. Kısa bir ara kontrol bile sonradan oluşacak tekrar
              iş riskini ciddi şekilde düşürür.
            </p>

            <h2 className={styles.seoTitle}>Silivri Bondex Sünger Satın Alırken Nelere Dikkat Edilmeli?</h2>
            <h3 className={styles.seoSubtitle}>Yoğunluk (kg/m³) değeri neden önemli?</h3>
            <p className={styles.seoText}>
              Yoğunluk değeri, ürünün mekanik davranışını ve belirli ses/titreşim senaryolarındaki performansını etkileyen temel parametrelerden
              biridir. Fakat bu değeri tek başına değerlendirmek doğru değildir. Yoğunluk, katman sistemi, yüzey ve uygulama kalitesi ile
              birlikte okunmalıdır.
            </p>
            <h3 className={styles.seoSubtitle}>Yanmazlık ve güvenlik özellikleri</h3>
            <p className={styles.seoText}>
              Projelerde güvenlik kriterleri önemlidir. Ürün seçiminde üretici teknik dokümanı, malzeme beyanı ve kullanım alanına uygunluk
              bilgileri birlikte incelenmelidir. Özellikle ticari projelerde bu belgelerin şeffaf şekilde sunulması, satın alma kararını
              güvenli hale getirir.
            </p>
            <h3 className={styles.seoSubtitle}>Uzun ömürlü kullanım için doğru seçim</h3>
            <p className={styles.seoText}>
              Uzun ömürlü performans için yalnızca başlangıç fiyatına bakmak yeterli değildir. Doğru kalınlık, doğru sistem ve doğru uygulama
              bir araya geldiğinde uzun vadeli memnuniyet oluşur. Bizim yaklaşımımız da tam olarak budur: kısa vadeli değil, sürdürülebilir
              sonuç üreten teknik kurgu.
            </p>
            <p className={styles.seoText}>
              Sahadaki deneyimimizden bildiğimiz en önemli gerçek, doğru kararın proje başında verildiğinde toplam maliyeti düşürdüğüdür.
              Bu nedenle keşif, metraj doğrulama ve teknik yönlendirme süreçlerini standart bir hizmet kalemi olarak yürütüyoruz.
            </p>

            <h2 className={styles.seoTitle}>Silivri Bondex Sünger Tedarik ve Hızlı Teslim Süreci</h2>
            <h3 className={styles.seoSubtitle}>Silivri içi hızlı teslim avantajı</h3>
            <p className={styles.seoText}>
              Silivri içi projelerde hızlı teslimat planı, şantiye erişimi ve uygulama takvimi ile birlikte hazırlanır. Amaç yalnızca hızlı
              getirmek değil, doğru zamanda doğru miktarı sahaya indirmektir. Bu yaklaşım, uygulama ekiplerinin bekleme süresini düşürür.
            </p>
            <h3 className={styles.seoSubtitle}>Özel ölçü kesim hizmeti var mı?</h3>
            <p className={styles.seoText}>
              Proje tipine göre özel ölçü ve kesim planı uygulanabilir. Özellikle düzensiz yüzeyli veya çok geçişli alanlarda doğru kesim
              planı, hem uygulama hızını artırır hem malzeme israfını azaltır.
            </p>
            <h3 className={styles.seoSubtitle}>Projeye göre ürün seçimi nasıl yapılır?</h3>
            <p className={styles.seoText}>
              Ürün seçimi; kullanım tipi, metraj, şikayet tanımı, hedef konfor seviyesi ve bütçe dengesi üzerinden yapılır. Bu nedenle her
              projede tek bir ürün önermek yerine seçenekli, teknik gerekçeli bir yaklaşım sunuyoruz. Uzmanlığımızı yalnızca ürün bilgisiyle
              değil, saha planlaması ve uygulama disipliniyle de destekliyoruz.
            </p>
            <p className={styles.seoText}>
              Projenizi paylaştığınızda keşif, teknik plan ve tedarik adımlarını tek süreçte yönetiyor; hızlı ama kontrolsüz değil, hızlı ve
              ölçülebilir sonuç üreten bir modelle ilerliyoruz.
            </p>
          </section>
        </div>
      ),
    },
    {
      key: 'teknik',
      label: 'Teknik Özellikler',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Teknik Özellikler Özeti</h2>
            <p className={styles.seoText}>
              Rebonded süngerpan yapı, darbe ve titreşim etkisinin yönetiminde güçlü bir temel sunar. En iyi sonuç, doğru ürün + doğru uygulama disiplini ile elde edilir.
            </p>
          </section>
        </div>
      ),
    },
    {
      key: 'kullanim',
      label: 'Kullanım Alanları',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Kullanım Alanları Özeti</h2>
            <p className={styles.seoText}>
              Konut, ofis, stüdyo, yazlık, teknik oda ve proje bazlı ticari alanlarda uygulanır.
            </p>
          </section>
        </div>
      ),
    },
    {
      key: 'opsiyonel',
      label: 'Opsiyonel',
      content: (
        <div className={styles.seoWrap}>
          <section className={styles.seoCard}>
            <h2 className={styles.seoTitle}>Lojistik ve Planlama Özeti</h2>
            <p className={styles.seoText}>
              Hızlı teslim, özel kesim ve proje bazlı teknik yönlendirme birlikte yönetilerek uygulama süreci optimize edilir.
            </p>
          </section>
        </div>
      ),
    },
  ];

  return (
    <main className="container ProductPage">
      <JsonLd data={schema} />

      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/">Anasayfa</Link>
        <span aria-hidden="true">/</span>
        <Link to="/urunler">Ürünler</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">Silivri Bondex Sünger</span>
      </nav>

      <div className={styles.heroGrid}>
        <div className={styles.media}>
          <img className={styles.img} src={silivriProduct.imagePath} alt="Silivri bondex sünger uygulaması" loading="eager" width="600" height="400" />
        </div>
        <section className={`ProductSEO card ${styles.panel}`}>
          <h1 className={styles.h1}>Silivri Bondex Sünger</h1>
          <p className={styles.modelLine}>
            <span className={styles.modelLabel}>Model:</span> BDX-SLV | Silivri İçi Projeler
          </p>
          <p className={styles.meta}>
            Silivri'de <strong>bondex sünger</strong> ihtiyacı olan konut, ofis, stüdyo ve teknik projelerde; deneyim, teknik uzmanlık ve
            belge odaklı süreç yönetimiyle kalıcı ses konforu hedefliyoruz.
          </p>

          {pageReviews.length > 0 && (
            <div className={styles.ratingRow}>
              <div className={styles.stars}>
                {[1, 2, 3, 4, 5].map((star) => (
                  <IconStar key={star} style={{ opacity: star <= Math.round(avgRating) ? 1 : 0.3 }} />
                ))}
              </div>
              <span className={styles.ratingText}>
                {avgRating.toFixed(1)} ({pageReviews.length} değerlendirme)
              </span>
            </div>
          )}

          {displayPrice && (
            <div className={styles.priceRow}>
              <span className={styles.price}>{displayPrice}</span>
              <span className={styles.priceUnit}>/ m²'den başlayan</span>
            </div>
          )}

          <div className={styles.badges}>
            <span className="chip">Bondex Sünger</span>
            <span className="chip">Silivri içi hızlı teslimat</span>
            <span className="chip">Uzman ekip + teknik dokümantasyon</span>
          </div>

          <div className={styles.specGrid} aria-label="Silivri için özet bilgiler">
            {topSpecs.map((spec) => (
              <div key={spec.k} className={styles.specItem}>
                <div className={styles.specK}>{spec.k}</div>
                <div className={styles.specV}>{spec.v}</div>
              </div>
            ))}
          </div>

          <div className={styles.ctaRow}>
            <a href={waUrl} target="_blank" rel="noopener noreferrer" className={`btn btn-primary ${styles.ctaBtn}`}>
              <IconWhatsApp />
              <span>WhatsApp ile Teklif Al</span>
            </a>
            <a href={`tel:${SALES_PHONE_READABLE.replace(/\s/g, '')}`} className={`btn btn-outline ${styles.ctaBtn}`}>
              <IconPhone />
              <span>{SALES_PHONE_READABLE}</span>
            </a>
          </div>
        </section>
      </div>

      <div className={styles.contentGrid}>
        <section>
          <div className={`card ${styles.sectionCard}`} style={{ padding: 0 }}>
            <Tabs items={tabItems} defaultKey="aciklama" />
          </div>
        </section>
      </div>

      {pageReviews.length > 0 && (
        <section className={styles.reviewSection}>
          <div className={styles.reviewHeader}>
            <h2 className={styles.reviewTitle}>Müşteri Değerlendirmeleri</h2>
            <div className={styles.reviewUnderline} aria-hidden="true" />
          </div>
          <div className={styles.reviewGrid}>
            {pageReviews.map((review, idx) => (
              <div key={idx} className={styles.reviewCard}>
                <div className={styles.reviewTop}>
                  <div className={styles.reviewStars}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <IconStar key={star} style={{ opacity: star <= review.rating ? 1 : 0.3 }} />
                    ))}
                  </div>
                  {review.verified && <span className={styles.verifiedBadge}>✓ Doğrulanmış</span>}
                </div>
                <h3 className={styles.reviewName}>{review.title}</h3>
                <p className={styles.reviewText}>{review.text}</p>
                <div className={styles.reviewFooter}>
                  <span className={styles.reviewAuthor}>{review.name}</span>
                  <span className={styles.reviewSeparator}>•</span>
                  <span className={styles.reviewDate}>{review.date}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {silivriProduct.faqs?.length ? (
        <section className={styles.faqSection}>
          <div className={styles.faqHeader}>
            <h2 className={styles.faqTitle}>Silivri Bondex Sünger – Sıkça Sorulan Sorular</h2>
            <div className={styles.faqUnderline} aria-hidden="true" />
          </div>
          <div className={styles.faqCard}>
            <Accordion items={silivriProduct.faqs} variant="seo" />
          </div>
        </section>
      ) : null}

      {relatedBondex.length ? (
        <section className={styles.relatedSection}>
          <div className={styles.relatedHeader}>
            <h2 className={styles.relatedTitle}>Diğer Bondex Sünger Modelleri</h2>
            <div className={styles.relatedUnderline} aria-hidden="true" />
          </div>
          <div className={styles.relatedGrid}>
            {relatedBondex.map((p) => (
              <div key={p.slug} className={styles.relatedItem}>
                <ProductCard product={p} />
              </div>
            ))}
          </div>
        </section>
      ) : null}
    </main>
  );
}
